import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bell, Pencil, Trash2, Check, X } from "lucide-react";
import { formatApiError } from "@/auth/AuthContext";
import { inputCls, labelCls } from "@/components/ChildForm";
import { fmtD } from "@/components/InvoiceParts";
import { isoDate } from "@/lib/dates";

function NoticeRow({ n, onSave, onDelete }) {
  const [edit, setEdit] = useState(false);
  const [message, setMessage] = useState(n.message);
  const [expiryDate, setExpiryDate] = useState(n.expiryDate);
  const save = async () => {
    if (!message.trim()) { toast.error("Write a notice message"); return; }
    await onSave(n.id, { message: message.trim(), expiryDate });
    setEdit(false);
  };
  if (edit) {
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-[var(--hp-border-hi)] px-3 py-2 text-xs" data-testid={`notice-row-${n.id}`}>
        <input data-testid={`notice-edit-message-${n.id}`} value={message} onChange={(e) => setMessage(e.target.value)} className={`${inputCls} flex-1 min-w-[180px] py-1.5`} />
        <input data-testid={`notice-edit-expiry-${n.id}`} type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} className={`${inputCls} w-auto py-1.5`} />
        <button data-testid={`notice-save-${n.id}`} onClick={save} className="grid h-7 w-7 place-items-center rounded-full text-[var(--hp-profit)] hover:bg-[var(--hp-primary)]/10"><Check className="h-4 w-4" /></button>
        <button data-testid={`notice-cancel-${n.id}`} onClick={() => setEdit(false)} className="grid h-7 w-7 place-items-center rounded-full text-[var(--hp-muted)] hover:bg-[var(--hp-tint-5)]"><X className="h-4 w-4" /></button>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-[var(--hp-border)] px-3 py-2 text-xs" data-testid={`notice-row-${n.id}`}>
      <span className="text-[var(--hp-heading)]">{n.message}</span>
      <span className="text-[var(--hp-muted)]">Expires {fmtD(n.expiryDate)}</span>
      <span className={`font-semibold ${n.active ? "text-[var(--hp-profit)]" : "text-[var(--hp-muted)]"}`} data-testid={`notice-status-${n.id}`}>{n.active ? "Showing now" : "Expired"}</span>
      <span className="ml-auto inline-flex items-center gap-1">
        <button data-testid={`notice-edit-${n.id}`} onClick={() => setEdit(true)} className="grid h-7 w-7 place-items-center rounded-full text-[var(--hp-muted)] hover:text-[var(--hp-profit)] hover:bg-[var(--hp-primary)]/10"><Pencil className="h-3.5 w-3.5" /></button>
        <button data-testid={`notice-delete-${n.id}`} onClick={() => onDelete(n)} className="grid h-7 w-7 place-items-center rounded-full text-[var(--hp-muted)] hover:text-[var(--hp-loss)]"><Trash2 className="h-3.5 w-3.5" /></button>
      </span>
    </div>
  );
}

export function StaffNoticesCard({ api }) {
  const today = isoDate();
  const [rows, setRows] = useState([]);
  const [message, setMessage] = useState("");
  const [expiryDate, setExpiryDate] = useState(today);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => axios.get(`${api}/kiosk/staff-notices`).then((r) => setRows(r.data)).catch(() => {}), [api]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!message.trim()) { toast.error("Write a notice message"); return; }
    setBusy(true);
    try { await axios.post(`${api}/kiosk/staff-notices`, { message: message.trim(), expiryDate }); toast.success("Notice added"); setMessage(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const save = async (id, patch) => {
    try { await axios.put(`${api}/kiosk/staff-notices/${id}`, patch); toast.success("Notice updated"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (n) => { if (!window.confirm("Delete this notice?")) return; try { await axios.delete(`${api}/kiosk/staff-notices/${n.id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <div className="rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] p-5 space-y-3" data-testid="staff-notices-card">
      <h3 className="font-head text-lg font-semibold text-[var(--hp-heading)] inline-flex items-center gap-2"><Bell className="h-4 w-4 text-[var(--hp-gold)]" /> Staff notices</h3>
      <p className="text-xs text-[var(--hp-muted)]">Shown on the kiosk staff board and pop up when a staff member signs in, until the expiry date you set — e.g. "Roster meeting Friday 3pm".</p>
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[220px]"><label className={labelCls}>Message</label><input data-testid="notice-message-input" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="e.g. Roster meeting Friday 3pm in the staff room" className={inputCls} /></div>
        <div><label className={labelCls}>Expires</label><input data-testid="notice-expiry-input" type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} className={`${inputCls} w-auto`} /></div>
        <button data-testid="notice-add-button" onClick={add} disabled={busy} className="inline-flex items-center gap-1.5 rounded-full bg-[var(--hp-primary)] px-4 py-2 text-xs font-bold text-[#04231a] disabled:opacity-40"><Bell className="h-3.5 w-3.5" /> Add notice</button>
      </div>
      {rows.length === 0 ? <p className="text-xs text-[var(--hp-muted)]" data-testid="staff-notices-empty">No notices yet.</p> : (
        <div className="space-y-1.5">{rows.map((n) => <NoticeRow key={n.id} n={n} onSave={save} onDelete={del} />)}</div>
      )}
    </div>
  );
}

export function StaffNoticesBoard({ api }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => axios.get(`${api}/kiosk/staff-notices/active`).then((r) => setRows(r.data)).catch(() => {}), [api]);
  useEffect(() => { load(); }, [load]);
  if (rows.length === 0) return null;
  return (
    <div className="rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] p-5 space-y-3" data-testid="staff-notices-board">
      <h3 className="font-head text-lg font-semibold text-[var(--hp-heading)] inline-flex items-center gap-2"><Bell className="h-4 w-4 text-[var(--hp-gold)]" /> Staff notices</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">{rows.map((n) => (
        <div key={n.id} className="rounded-xl border border-[var(--hp-gold)]/30 bg-[var(--hp-gold)]/10 p-3 text-sm" data-testid={`staff-notice-${n.id}`}>
          <p className="text-[var(--hp-text)]">{n.message}</p>
          <p className="text-xs text-[var(--hp-muted)] mt-1">Until {fmtD(n.expiryDate)}</p>
        </div>
      ))}</div>
    </div>
  );
}

export function StaffNoticesPopup({ api, onClose }) {
  const [notices, setNotices] = useState(null);
  useEffect(() => { axios.get(`${api}/kiosk/staff-notices/active`).then((r) => setNotices(r.data)).catch(() => setNotices([])); }, [api]);
  if (!notices || notices.length === 0) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4" data-testid="staff-notices-popup">
      <div className="w-full max-w-md rounded-3xl border border-[var(--hp-border)] bg-[var(--hp-card)] p-6 space-y-4">
        <p className="font-head text-lg font-semibold text-[var(--hp-heading)] inline-flex items-center gap-2"><Bell className="h-5 w-5 text-[var(--hp-gold)]" /> {notices.length > 1 ? "Staff notices" : "Staff notice"}</p>
        <div className="space-y-2">{notices.map((n) => <p key={n.id} className="text-sm text-[var(--hp-text)] rounded-xl border border-[var(--hp-gold)]/30 bg-[var(--hp-gold)]/10 p-3" data-testid={`staff-notice-popup-${n.id}`}>{n.message}</p>)}</div>
        <button data-testid="staff-notices-dismiss" onClick={onClose} className="w-full rounded-xl bg-[var(--hp-primary)] py-3 text-sm font-bold text-[#04231a]">Got it</button>
      </div>
    </div>
  );
}
